import {
  CuriosityAutocompleteValueWithNull,
  CuriosityTranslation,
  UseCuriosityAutocompleteResult,
} from '@procureai/curiosity-ui-mui';
import { any } from 'ramda';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';

import { isString } from '~/util/string';

import { UserHitId, UserEntity } from './UserAutocomplete.types';

export const createStaticHit = (id: UserHitId, value: string, key?: string): CuriosityTranslation<UserEntity> => ({
  id,
  key: key || id,
  value: value,
  language: '',
  entity: {
    firstName: '',
    lastName: '',
    email: '',
  },
});

export interface UseUserAutocompleteProps<
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined = false,
> extends UseCuriosityAutocompleteResult<UserEntity, Multiple, DisableClearable> {
  hasEmptyOption?: boolean;
  translationPrefix?: string;
}

export const useUserAutocomplete = <
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
>({
  options: optionsProp,
  onChange: onChangeProp,
  hasEmptyOption,
  translationPrefix,
  ...otherProps
}: UseUserAutocompleteProps<Multiple, DisableClearable>): UseCuriosityAutocompleteResult<
  UserEntity,
  Multiple,
  DisableClearable
> => {
  const { t } = useTranslation(undefined, { keyPrefix: translationPrefix ?? 'UserAutocomplete' });
  const { user } = useAuth();

  const { value, multiple } = otherProps;

  const valueArr: CuriosityAutocompleteValueWithNull<UserEntity> = multiple ? (value as any) : [value];

  const currentUserId = user?.profile.sub;
  const currentUserSelected = any((val) => {
    if (val) {
      return (isString(val) ? val : val.key) === currentUserId;
    }
    return false;
  }, valueArr);
  const isAssigned = !!value && valueArr.length > 0;

  const options: CuriosityTranslation<UserEntity>[] = useMemo(() => {
    const staticItems: CuriosityTranslation<UserEntity>[] = [];
    if (currentUserId && !currentUserSelected) {
      staticItems.push(createStaticHit(UserHitId.ME, t(UserHitId.ME), currentUserId));
    }
    if (isAssigned && !multiple) {
      staticItems.push(createStaticHit(UserHitId.UNASSIGNED, t(UserHitId.UNASSIGNED)));
    }
    if (hasEmptyOption && multiple) {
      staticItems.push(createStaticHit(UserHitId.EMPTY, t(UserHitId.EMPTY)));
    }
    return staticItems.concat(optionsProp as any);
  }, [t, currentUserId, optionsProp, currentUserSelected, isAssigned, multiple, hasEmptyOption]);

  return {
    options,
    onChange: (event, val, reason, details) => {
      if (onChangeProp) {
        const nextArr: CuriosityAutocompleteValueWithNull<UserEntity> = multiple ? (val as any) : [val];
        const mapped = nextArr.map((changedValue) => {
          if (changedValue && typeof changedValue === 'object') {
            switch (changedValue.id) {
              case UserHitId.UNASSIGNED:
                return null;
              case UserHitId.ME:
                return changedValue.key;
            }
          }
          return changedValue;
        });
        onChangeProp(event, multiple ? (mapped as any) : mapped[0], reason, details);
      }
    },
    ...otherProps,
  };
};
